/**
 * Wrapped select with label and error display.
 *
 * Props:
 *   id        — ties the <label> htmlFor to the <select> id (required for a11y in block mode)
 *   label     — label text; omit to skip the label element (ignored when `inline`)
 *   error     — error string; renders below the select when truthy (ignored when `inline`)
 *   inline    — when true, renders a bare <select> with no wrapping .form-group or
 *               label, for dropping into an existing flex filter bar (e.g. `.filters`).
 *               Caller supplies its own accessible label (aria-label) and the
 *               surrounding container provides the visual styling.
 *   variant   — 'pill' renders a bare <select> styled as a dark-surface pill
 *               (rounded, sits on the sidebar/header colour) with the native OS
 *               option picker preserved. Like `inline`, it skips the .form-group
 *               wrapper and label, so the caller supplies an aria-label. Used by
 *               the mobile session header's asset-type control. Per-option icons
 *               are intentionally not supported — a native <select> can't render
 *               them and going custom would cost the OS picker (see DESIGN_SYSTEM).
 *   className — extra class(es); wrapping <div> in block mode, the <select> itself
 *               in inline / pill mode
 *   children  — <option> elements
 *
 * All other props (value, onChange, disabled, ...) pass to <select>.
 */
import { forwardRef } from 'react';

const Select = forwardRef(function Select(
  { id, label, error, className = '', children, inline = false, variant, ...props },
  ref
) {
  if (variant === 'pill') {
    return (
      <select
        id={id}
        ref={ref}
        className={['select-pill', className].filter(Boolean).join(' ')}
        aria-invalid={error ? 'true' : undefined}
        {...props}
      >
        {children}
      </select>
    );
  }

  if (inline) {
    return (
      <select
        id={id}
        ref={ref}
        className={className || undefined}
        aria-invalid={error ? 'true' : undefined}
        {...props}
      >
        {children}
      </select>
    );
  }

  return (
    <div className={['form-group', className].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
        </label>
      )}
      <select
        id={id}
        ref={ref}
        className={['form-input', error ? 'form-input--error' : ''].filter(Boolean).join(' ')}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p id={`${id}-error`} className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

export default Select;
